/**
 * The one socket. Created lazily so the `?demo` harness — which renders every
 * screen from fixtures — never opens a connection.
 *
 * The three proactive reconnect hooks below are ported from hexagon: they fix
 * real mobile failure modes that socket.io-client's own backoff does not, and
 * without them the recovery is "the player refreshes the page".
 */
import { io, type Socket } from 'socket.io-client'
import { getSessionToken } from '../auth/session'
import { API_BASE } from '../apiBase'

// Shared with the REST calls on purpose: a session established against one
// origin and a socket opened against another authenticates as nobody.

let instance: Socket | null = null

export function getSocket(): Socket {
  if (instance) return instance
  instance = io(API_BASE, {
    autoConnect: true,
    // Identity is server-asserted: the handshake carries the session token and
    // the server resolves the userId, so there is no client-supplied id to spoof.
    auth: { sessionToken: getSessionToken() },
    // Straight to a websocket, rather than socket.io's default of long-polling
    // first and upgrading once that works: the poll handshake costs two extra
    // round trips before the table can arrive, and on a phone's connection
    // that measured as half the time from opening the app to the first
    // snapshot (tools/bench-frontend.mjs, docs/PERFORMANCE.md). A network that
    // will not carry a websocket still gets polling, which is what
    // `tryAllTransports` is for.
    transports: ['websocket', 'polling'],
    tryAllTransports: true,
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 3000,
    reconnectionAttempts: Infinity,
    timeout: 15000,
  })
  installReconnectHooks(instance)
  return instance
}

/**
 * socket.io-client buffers everything emitted while the transport is down and
 * flushes it the instant it reconnects — before any `connect` listener can run,
 * which is why this has to happen on the way down rather than on the way up.
 *
 * A player's action means "this card, in this trick, now". Replaying it after a
 * blackout aims it at a table that has moved on: a tap made during an 8 s
 * outage arrived 8.1 s later against a different hand (VERDICT B7). Dropping it
 * costs the player one tap; delivering it costs them the wrong card.
 */
function dropBufferedEmits(socket: Socket): void {
  socket.sendBuffer.length = 0
}

/** After a passkey ceremony the handshake payload is stale — reconnect with it. */
export function reauthenticateSocket(): void {
  if (!instance) return
  instance.auth = { sessionToken: getSessionToken() }
  instance.disconnect()
  instance.connect()
}

function installReconnectHooks(socket: Socket): void {
  if (typeof window !== 'undefined') {
    // 1. Mobile tab suspension kills the WebSocket silently, and the
    //    reconnection timer scheduled before the freeze may never fire.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !socket.connected) socket.connect()
    })
    // 2. Connectivity came back — don't sit out the remaining backoff.
    window.addEventListener('online', () => {
      if (!socket.connected) socket.connect()
    })
  }
  // 3. socket.io-client documents that it will NOT auto-reconnect after an
  //    explicit server-side disconnect. We have to kick it ourselves.
  socket.on('disconnect', (reason) => {
    console.debug('[socket] disconnected:', reason)
    dropBufferedEmits(socket)
    if (reason === 'io server disconnect') socket.connect()
  })
}
